import { useEffect, useMemo, useRef, useState } from "react";
import { Check, X, TimerReset } from "lucide-react";

const GOLD = "#CEAB65";

type QuizItem = {
  q_fr: string;
  q_en: string;
  choices_fr: string[];
  choices_en: string[];
  answer: number;
  explain_fr?: string;
  explain_en?: string;
};

export function QuizEngine({
  quiz,
  quizId,
  lang = "en",
  durationSec = 180,
  onSubmit,
}: {
  quiz: QuizItem[];
  quizId: string;
  lang?: "fr" | "en";
  durationSec?: number;
  onSubmit?: (score: number) => void;
}) {
  const [answers, setAnswers] = useState<number[]>(() => loadLocal(quizId, quiz.length));
  const [submitted, setSubmitted] = useState<boolean>(() => loadSubmitted(quizId));
  const [left, setLeft] = useState<number>(() => loadTime(quizId, durationSec));
  const tickRef = useRef<number | null>(null);

  useEffect(() => saveLocal(quizId, answers, submitted, left), [quizId, answers, submitted, left]);

  useEffect(() => {
    if (submitted || left <= 0) return;
    tickRef.current = window.setInterval(() => setLeft((s) => (s > 0 ? s - 1 : 0)), 1000) as any;
    return () => tickRef.current && clearInterval(tickRef.current);
  }, [submitted, left]);

  const t = (s_fr: string, s_en: string) => (lang === "fr" ? s_fr : s_en);

  const score = useMemo(() => answers.reduce((s, a, i) => (a === quiz[i].answer ? s + 1 : s), 0), [answers, quiz]);
  const allAnswered = answers.every((a) => a >= 0);

  function reset() {
    setAnswers(Array(quiz.length).fill(-1));
    setSubmitted(false);
    setLeft(durationSec);
  }

  return (
    <div className="rounded-2xl border border-white/10 bg-white/5 p-4">
      <div className="mb-4 flex items-center justify-between gap-3">
        <div className="text-sm text-white/80">
          {t("Temps restant", "Time left")}: {formatTime(left)}
        </div>
        <button
          onClick={reset}
          className="inline-flex items-center gap-1 rounded-xl border border-white/10 px-3 py-1.5 text-xs hover:bg-white/10"
        >
          <TimerReset className="h-4 w-4" /> {t("Réinitialiser", "Reset")}
        </button>
      </div>

      {quiz.map((it, i) => {
        const answered = answers[i] >= 0;
        const correct = answered && answers[i] === it.answer;
        return (
          <div key={i} className="mb-4 rounded-xl bg-white/5 p-3">
            <p className="font-medium">
              {i + 1}. {t(it.q_fr, it.q_en)}
            </p>
            <div className="mt-2 grid gap-2">
              {(lang === "fr" ? it.choices_fr : it.choices_en).map((c, j) => {
                const isChecked = answers[i] === j;
                const showState = submitted || left === 0;
                const isAnswer = j === it.answer;
                return (
                  <label
                    key={j}
                    className={`flex cursor-pointer items-center justify-between gap-2 rounded-lg p-2 transition ${
                      isChecked ? "bg-white/15" : "bg-white/5 hover:bg-white/10"
                    } ${showState && isAnswer ? "ring-1 ring-emerald-400/60" : ""}`}
                  >
                    <span className="flex items-center gap-2">
                      <input
                        type="radio"
                        name={`q${i}`}
                        className="accent-emerald-400"
                        disabled={submitted || left === 0}
                        checked={isChecked}
                        onChange={() => setAnswers((arr) => arr.map((v, idx) => (idx === i ? j : v)))}
                      />
                      <span>{c}</span>
                    </span>
                    {showState && (
                      <span>
                        {isAnswer ? (
                          <Check className="h-4 w-4 text-emerald-300" />
                        ) : isChecked ? (
                          <X className="h-4 w-4 text-red-300" />
                        ) : null}
                      </span>
                    )}
                  </label>
                );
              })}
            </div>
            {(submitted || left === 0) && (answered || it.explain_fr || it.explain_en) ? (
              <p className={`mt-2 text-sm ${correct ? "text-emerald-200" : "text-white/80"}`}>
                {correct 
                  ? t("Bonne réponse!", "Correct!") 
                  : t("Réponse correcte :", "Correct answer:") + " " + (lang === "fr" ? it.choices_fr[it.answer] : it.choices_en[it.answer])
                }
                {" "}
                {it.explain_fr || it.explain_en ? `— ${t(it.explain_fr || "", it.explain_en || "")}` : ""}
              </p>
            ) : null}
          </div>
        );
      })}

      <div className="mt-4 flex items-center justify-between">
        <div className="text-white/80">
          {t("Score", "Score")}: {score} / {quiz.length}
        </div>
        <button
          disabled={submitted || left === 0 || !allAnswered}
          onClick={() => {
            setSubmitted(true);
            if (typeof onSubmit === 'function') onSubmit(score);
          }}
          className="rounded-xl px-3 py-2 font-semibold text-black disabled:cursor-not-allowed disabled:opacity-60"
          style={{ backgroundColor: GOLD }}
        >
          {t("Valider", "Submit")}
        </button>
      </div>
      {(submitted || left === 0) && (
        <p className="mt-3 text-sm text-white/80">
          {t("Résultat enregistré localement.", "Result saved locally.")}
        </p>
      )}
    </div>
  );
}

function formatTime(s: number) {
  const m = Math.floor(s / 60);
  const r = s % 60;
  return `${m}:${r.toString().padStart(2, "0")}`;
}

function loadLocal(key: string, len: number): number[] {
  try {
    const raw = localStorage.getItem(`quiz:${key}:answers`);
    if (!raw) return Array(len).fill(-1);
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : Array(len).fill(-1);
  } catch {
    return Array(len).fill(-1);
  }
}

function loadSubmitted(key: string): boolean {
  try {
    return localStorage.getItem(`quiz:${key}:submitted`) === "1";
  } catch {
    return false;
  }
}

function loadTime(key: string, duration: number): number {
  try {
    const t = localStorage.getItem(`quiz:${key}:left`);
    return t ? Math.max(0, Number(t)) : duration;
  } catch {
    return duration;
  }
}

function saveLocal(key: string, answers: number[], submitted: boolean, left: number) {
  try {
    localStorage.setItem(`quiz:${key}:answers`, JSON.stringify(answers));
    localStorage.setItem(`quiz:${key}:submitted`, submitted ? "1" : "0");
    localStorage.setItem(`quiz:${key}:left`, String(left));
  } catch {}
}
